import { useEffect, useState } from "react";
import { PageHead, Panel, Stat } from "@/components/kit";
import { api, fmtMoney } from "@/lib/api";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const delta = (a, b) => (b ? `${a >= b ? "+" : ""}${Math.round(((a - b) / b) * 100)}% sull'anno prima` : "");

function Counts({ title, obj, tot, testId }) {
  const rows = Object.entries(obj).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-[#7B929C]">{title}</div>
      {rows.length ? rows.map(([k, v]) => <div key={k} className="flex items-center gap-2 text-sm"><span className="w-40 shrink-0 truncate" title={k}>{k}</span><div className="flex-1 h-2 rounded-full bg-[#FEF2F2] overflow-hidden"><div className="h-full bg-[#F87171]" style={{ width: `${tot ? (v / tot) * 100 : 0}%` }} /></div><span className="text-xs text-[#7B929C] w-8 text-right">{v}</span></div>) : <div className="text-sm text-[#7B929C]">—</div>}
    </div>
  );
}

function Lost({ p }) {
  return (
    <Panel title="Perché perdiamo i clienti" testId="report-lost" right={<span className="text-xs text-[#7B929C]">{p.totale} richieste perse • {p.rifiutati} preventivi rifiutati</span>}>
      {p.totale === 0 ? <div className="text-sm text-[#7B929C]">Nessuna richiesta persa nell'anno. Quando segnate una richiesta come «perso», indicate il motivo.</div> : (
        <div className="grid md:grid-cols-3 gap-6">
          <Counts title="Motivo" obj={p.motivi} tot={p.totale} testId="report-lost-reasons" />
          <Counts title="Da dove arrivavano" obj={p.fonti} tot={p.totale} testId="report-lost-sources" />
          <Counts title="Struttura richiesta" obj={p.sedi} tot={p.totale} testId="report-lost-sedi" />
        </div>
      )}
    </Panel>
  );
}

function Bars({ cur, prev }) {
  const max = Math.max(1, ...cur.map((m) => m.valore), ...prev.map((m) => m.valore));
  return (
    <div className="flex items-end gap-1.5 h-48" data-testid="report-bars">
      {cur.map((m, i) => (
        <div key={m.mese} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${m.eventi} eventi • ${fmtMoney(m.valore)}`}>
          <div className="w-full flex items-end gap-0.5 h-40">
            <div className="flex-1 bg-[#D4E6EC] rounded-t" style={{ height: `${(prev[i].valore / max) * 100}%` }} />
            <div className="flex-1 bg-gradient-to-t from-[#0B6E8F] to-[#34B3B0] rounded-t" style={{ height: `${(m.valore / max) * 100}%` }} />
          </div>
          <div className="text-[10px] text-[#7B929C]">{MESI[i]}</div>
        </div>
      ))}
    </div>
  );
}

export default function Report() {
  const [y, setY] = useState(new Date().getFullYear());
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get(`/report?anno=${y}`).then((x) => setR(x.data));
  }, [y]);
  if (!r) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const c = r.corrente, p = r.precedente;
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Andamento per la direzione" title="Report">
        <select className="rounded-full border border-[#D4E6EC] px-4 py-2 text-sm bg-white" value={y} onChange={(e) => setY(+e.target.value)} data-testid="report-year">{[0, 1, 2, -1].map((d) => new Date().getFullYear() - d).map((v) => <option key={v}>{v}</option>)}</select>
      </PageHead>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat dark label={`Valore eventi ${y}`} value={fmtMoney(c.valore)} sub={delta(c.valore, p.valore)} testId="report-value" />
        <Stat label="Eventi firmati" value={c.eventi} sub={delta(c.eventi, p.eventi)} testId="report-events" />
        <Stat label="Spesa media per ospite" value={fmtMoney(c.per_ospite)} sub={`${c.ospiti} ospiti in tutto`} testId="report-per-guest" />
        <Stat label="Incassato" value={fmtMoney(c.incassato)} sub={delta(c.incassato, p.incassato)} testId="report-cashed" />
      </div>
      <Panel title="Valore eventi mese per mese" right={<div className="text-xs text-[#7B929C] flex gap-3"><span><i className="inline-block w-3 h-3 rounded-sm bg-[#0B6E8F] mr-1 align-middle" />{y}</span><span><i className="inline-block w-3 h-3 rounded-sm bg-[#D4E6EC] mr-1 align-middle" />{y - 1}</span></div>}><Bars cur={c.mesi} prev={p.mesi} /></Panel>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Per struttura" testId="report-sedi">
          {Object.entries(c.sedi).length ? Object.entries(c.sedi).map(([s, v]) => <div key={s} className="flex justify-between text-sm border-b border-[#EAF5F7] py-2"><span>{s}</span><span className="text-right">{v.eventi} eventi • <b>{fmtMoney(v.valore)}</b><br /><span className="text-xs text-[#7B929C]">{v.ospiti ? `${fmtMoney(v.valore / v.ospiti)} a ospite` : ""}</span></span></div>) : <div className="text-sm text-[#7B929C]">Nessun evento firmato nell'anno.</div>}
        </Panel>
        <Panel title="Vendite" testId="report-sales">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="font-serif-it text-3xl">{r.preventivi.perc}%</div><div className="text-xs text-[#7B929C]">preventivi accettati ({r.preventivi.accettati}/{r.preventivi.inviati})</div></div>
            <div><div className="font-serif-it text-3xl">{r.risposta_ore ?? "—"}{r.risposta_ore != null && "h"}</div><div className="text-xs text-[#7B929C]">tempo medio di prima risposta</div></div>
            <div><div className="font-serif-it text-3xl">{r.soddisfazione.media ?? "—"}</div><div className="text-xs text-[#7B929C]">voto medio clienti ({r.soddisfazione.risposte})</div></div>
          </div>
          <div className="space-y-1 pt-2">{Object.entries(r.fonti).sort((a, b) => b[1].totale - a[1].totale).map(([f, v]) => <div key={f} className="flex items-center gap-2 text-sm"><span className="w-32 shrink-0">{f}</span><div className="flex-1 h-2 rounded-full bg-[#EAF5F7] overflow-hidden"><div className="h-full bg-[#34B3B0]" style={{ width: `${v.totale ? (v.vinti / v.totale) * 100 : 0}%` }} /></div><span className="text-xs text-[#7B929C] w-24 text-right">{v.vinti}/{v.totale} firmati</span></div>)}</div>
        </Panel>
      </div>
      <Lost p={r.persi} />
    </div>
  );
}
